import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { GqlAgendaItemKind, GqlMeetingAgendaTemplateItem } from '../../graphql/base-types.graphql';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Button } from '@startuptools/ui/Button';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMatiCreateMutation, useMatiUpdateMutation } from './BoardMeetingTemplateItemDialog.graphql';
import { BoardMeetingTemplatesDocument } from './BoardMeetingTemplates.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useState } from 'react';
import { RichTextInput } from '@startuptools/ui/RichTextInput';

type FormValues = Pick<
  GqlMeetingAgendaTemplateItem,
  | 'order'
  | 'kind'
  | 'subjectPrimaryLang'
  | 'subjectSecondaryLang'
  | 'mandatory'
  | 'decisionSecondaryLang'
  | 'decisionPrimaryLang'
  | 'commentPrimaryLang'
  | 'commentSecondaryLang'
  | 'firstGroup'
  | 'lastGroup'
>;

export const BoardMeetingTemplateItemDialog = ({
  mati,
  meetingAgendaTemplateId,
  movableRange,
  onClose,
}: {
  mati?: Pick<
    GqlMeetingAgendaTemplateItem,
    | 'id'
    | 'order'
    | 'kind'
    | 'subjectPrimaryLang'
    | 'subjectSecondaryLang'
    | 'mandatory'
    | 'commentPrimaryLang'
    | 'commentSecondaryLang'
    | 'decisionPrimaryLang'
    | 'decisionSecondaryLang'
    | 'firstGroup'
    | 'lastGroup'
  >;
  meetingAgendaTemplateId?: string;
  movableRange: number[];
  onClose: () => void;
}) => {
  const companyId = useCompanyId();
  const [selectedKind, setSelectedKind] = useState<GqlAgendaItemKind>(mati?.kind ?? GqlAgendaItemKind.Standard);
  const { control, register, handleSubmit } = useForm<FormValues>({
    defaultValues: mati
      ? {
          order: mati.order,
          subjectPrimaryLang: mati.subjectPrimaryLang,
          subjectSecondaryLang: mati.subjectSecondaryLang,
          mandatory: mati.mandatory,
          decisionSecondaryLang: mati.decisionSecondaryLang,
          decisionPrimaryLang: mati.decisionPrimaryLang,
          commentPrimaryLang: mati.commentPrimaryLang,
          commentSecondaryLang: mati.commentSecondaryLang,
        }
      : {
          order: (movableRange.at(-1) ?? 3) + 1,
          mandatory: false,
          kind: GqlAgendaItemKind.Standard,
          firstGroup: false,
          lastGroup: false,
        },
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doEdit] = useMatiUpdateMutation({ refetchQueries: [BoardMeetingTemplatesDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate] = useMatiCreateMutation({ refetchQueries: [BoardMeetingTemplatesDocument] });

  const onSubmit: SubmitHandler<FormValues> = async ({ commentPrimaryLang, commentSecondaryLang, ...values }) => {
    if (mati) {
      await doEdit({
        variables: {
          companyId,
          input: {
            ...values,
            commentPrimaryLang,
            commentSecondaryLang,
            id: mati.id,
          },
        },
      });
    } else if (meetingAgendaTemplateId) {
      await doCreate({
        variables: {
          companyId,
          input: {
            ...values,
            commentPrimaryLang,
            commentSecondaryLang,
            meetingAgendaTemplateId,
          },
        },
      });
    }
    onClose();
  };

  return (
    <Modal open onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalDialog minWidth={700}>
          <ModalClose />
          <Stack pt={3} gap={2}>
            <Typography level="h4">
              {mati ? <Trans id="Edit Agenda Item" /> : <Trans id="Add Agenda Item" />}
            </Typography>

            {!mati && (
              <Controller
                control={control}
                name="kind"
                render={({ field: { onChange, ...field } }) => (
                  <FormControl>
                    <FormLabel>
                      <Trans id="Type of Agenda Item" />
                    </FormLabel>
                    <Select
                      {...field}
                      onChange={(_, v) => {
                        onChange(v);
                        setSelectedKind(v ?? GqlAgendaItemKind.Standard);
                      }}
                    >
                      <Option value={GqlAgendaItemKind.Standard}>
                        <Trans id="Generic" />
                      </Option>
                      <Option value={GqlAgendaItemKind.PriorMeetingApproval}>
                        <Trans id="Approval of meeting minutes for previous meeting" />
                      </Option>
                      <Option value={GqlAgendaItemKind.NextMeeting}>
                        <Trans id="Next meeting" />
                      </Option>
                    </Select>
                  </FormControl>
                )}
              />
            )}

            {selectedKind === GqlAgendaItemKind.Standard && (
              <>
                <Stack direction="row" gap={2} alignItems="center" flexGrow={1}>
                  <FormControl>
                    <FormLabel>
                      <Trans id="Position" />
                    </FormLabel>
                    <Input {...register('order')} />
                  </FormControl>

                  <Checkbox sx={{ pt: 3 }} label={<Trans id="Mandatory Agenda Item" />} />
                </Stack>

                <Stack direction="row" gap={2} flexGrow={1}>
                  <FormControl sx={{ flexGrow: 1 }}>
                    <FormLabel>
                      <Trans id="Subject" />
                    </FormLabel>
                    <Input {...register('subjectPrimaryLang')} />
                  </FormControl>

                  <FormControl sx={{ flexGrow: 1 }}>
                    <FormLabel>
                      <Trans id="Subject (eng)" />
                    </FormLabel>
                    <Input {...register('subjectSecondaryLang')} />
                  </FormControl>
                </Stack>

                <Controller
                  control={control}
                  name="commentPrimaryLang"
                  render={({ field: { onChange, value } }) => (
                    <FormControl>
                      <FormLabel>
                        <Trans id="Comment" />
                      </FormLabel>
                      <RichTextInput value={value} onChange={onChange} />
                    </FormControl>
                  )}
                />

                <Controller
                  control={control}
                  name="commentSecondaryLang"
                  render={({ field: { onChange, value } }) => (
                    <FormControl>
                      <FormLabel>
                        <Trans id="Comment (eng)" />
                      </FormLabel>
                      <RichTextInput value={value} onChange={onChange} />
                    </FormControl>
                  )}
                />

                <Stack direction="row" gap={2} flexGrow={1}>
                  <FormControl sx={{ flexGrow: 1 }}>
                    <FormLabel>
                      <Trans id="Decision" />
                    </FormLabel>
                    <Input {...register('decisionPrimaryLang')} />
                  </FormControl>

                  <FormControl sx={{ flexGrow: 1 }}>
                    <FormLabel>
                      <Trans id="Decision (eng)" />
                    </FormLabel>
                    <Input {...register('decisionSecondaryLang')} />
                  </FormControl>
                </Stack>
              </>
            )}

            <Stack direction="row" justifyContent="end" gap={1}>
              <Button variant="outlined" color="neutral" onClick={onClose}>
                <Trans id="Cancel" />
              </Button>
              <Button type="submit">
                <Trans id="Save" />
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </form>
    </Modal>
  );
};
